// @ts-nocheck
import { ChannelType } from '@shopee/shopee-chatbot-agent-chat/dist/types';

import coreStore from '@/stores/core';
import sessionStore from '@/stores/session';
import API from '@/services';
import API_CONFIG, {
  BOT_MESSAGE_API,
  CSAT_BOT_INTERACTION_API,
  LIVE_AGENT_CONTROL_API,
  REPLIES_APIS,
} from '@/services/apis';
import { AutoQueueFromType, LiveAgentRecommenderScenario } from '@/types/intent';
import { DIALOGUE_TYPE } from '@/types/common';
import { QueueType } from '@/interfaces';
import { ENTRY_POINT } from '@/types/entryPoints';
import firstInitStore from '@/stores/firstInit';
import { useUIOptimisationRecommender } from '@/utils/common/UIOptimisation/Recommender';
import { useUIOptimisationHotQuestion } from '@/utils/common/UIOptimisation/HotQuestion';

import { replaceSkeletonDialogues, stashInitSessionDialogue } from './firstScreenSkeleton';
import { isBatchShowSkeleton } from './firstScreenSkeletonControl';
import { INPUT_TYPE, IntentSource, LiveAgentControl } from './constants';
import RESPONSE_HANDLER, { REPLY_TYPE } from './responseHandler';
import { addDialogue } from './sessionUtils';
import reporter from './report';
import { getCallbackParams } from './common';
import { isOldUI, isSeparate } from './experiment/getArticleUIUXExperimen';
import { isLiveAgentScUIOptimisation } from './experiment/getShortCutUIUXExperiment';
import { animate } from './animate';
import { addUserInputInfo } from './replyTimeCostReport';

export * from './strings';

/**
 * @member {boolean} withNoRes - no need handle by handle Response
 */
interface IOptions {
  inputType?: any;

  // false
  noLoading?: boolean;
  // false
  firstScreen?: boolean;
  // false
  requestNoWait?: boolean;
  // false
  withNoRes?: boolean;
  [key: string]: any;
}

export type IAPI_CONFIG_REQUEST = keyof typeof API_CONFIG | string;

export type ISuccessBodyData = Record<string, any>;
export type IErrorBodyData = undefined | null;
export type IHandleRequestBodyData = ISuccessBodyData | IErrorBodyData;

// 处理请求发送
export const handleRequest = async <
  IBodyData extends ISuccessBodyData = any
>(request: IAPI_CONFIG_REQUEST, params: Record<string, any>, options: IOptions = {}): Promise<IBodyData | IErrorBodyData> => {
  let userInputId;
  if (REPLIES_APIS.includes(request)) {
    userInputId = addUserInputInfo(options?.inputType);
  }

  const timeout = setTimeout(() => {
    !options.noLoading && sessionStore.SHOW_LOADING();
  }, 200);
  const botMessageApiList = Reflect.ownKeys(BOT_MESSAGE_API);
  const csatBotInteractionApiList = Reflect.ownKeys(CSAT_BOT_INTERACTION_API);
  if (!options.firstScreen) {
    await coreStore.fullStorePromise;
  }
  const entryPoint = ENTRY_POINT[coreStore.safeGetStore('initStore').from] ?? ENTRY_POINT.general;

  if (botMessageApiList.includes(request)) {
    // message 发送
    reporter.apiBatchReport({ type: 'count', value: { label: 'func_bot_msg' }});
  }

  // 当请求是 message 时 先等待 init session 返回 再请求 并且补充 session id
  if (request === API_CONFIG.postMessage.name) {
    // 当请求是 message 时，需要重置 input box 的 inputHasBeenTyped 状态为 false
    await firstInitStore.initApiPromise;
    if (!params.session_id) {
      reporter.apiLog(`【request】postMessage empty sessionId; params: ${JSON.stringify(params)}`, { level: 'debug' });
    }
    params.session_id = coreStore.safeGetStore('initStore').sessionId;
  }

  // 还是输入中状态时 等待当前的 promise 结束后再请求
  if (coreStore.safeGetStore('inputStore').contextNluTyping.isTyping && !options.requestNoWait) {
    await coreStore.safeGetStore('inputStore').handleRequestMessage({ trigger: true });
  }

  // 当请求是 /article 时 如果两个需要的参数都没有就上报
  if (request === API_CONFIG.getArticle.name || request === API_CONFIG.getArticleContent.name) {
    if (!params?.article_num && !params?.kb_intent_id) {
      const sessionID = coreStore.safeGetStore('initStore').sessionId;
      const userID = coreStore.safeGetStore('userStore').userId;
      const chatbotUserID = coreStore.safeGetStore('userStore').chatbot_user_id;
      const reportIDText = `session ID: ${sessionID} user ID: ${userID} chatbot user ID: ${chatbotUserID}`;
      reporter.apiLog(
        `【request】getArticle empty article_num and empty kb_intent_id; params: ${JSON.stringify(
          params,
        )} ${reportIDText}`,
        { level: 'debug' },
      );
    }
  }

  if (request === API_CONFIG.postMessage.name && params) {
    params.entry_point = entryPoint;
  }

  // 如果是 这两个接口 就携带 app version
  if (request === API_CONFIG.postMessage.name || request === API_CONFIG.getIntentSolution.name) {
    params.app_version = params.app_version ?? window?.getAppVersion?.();
  }

  return API[request](params)
    .then((res) => {
      if (botMessageApiList.includes(request)) {
        // message 发送成功
        reporter.apiBatchReport({ type: 'count', value: { label: 'func_bot_msg_ok' }});
      }

      const passCsatBotInteractionParamsCheck = params.input_type !== INPUT_TYPE.INPUT_BY_CLICK_OTHER_FREQUENTLY_ASKED;
      if (csatBotInteractionApiList.includes(request) && passCsatBotInteractionParamsCheck) {
        sessionStore.UPDATE_INTERACTED_WITH_BOT();
      }

      clearTimeout(timeout);
      sessionStore.HIDE_LOADING();

      // TODO 判断是否需要登录重发
      // eslint-disable-next-line
      !options.withNoRes &&
        handleResponse({
          res,
          // 请求信息，Login类型组件需要
          req: {
            request,
            params,
            curPos: options.curPos, // 当前user向输出dialogue的pos信息
            userInputId, // 用户输入（type 或 click）的 id 标记
          },
        });
      return res;
    })
    .catch(async (error) => {
      // 等待store装载完毕
      await coreStore.fullStorePromise;
      const agentStore = coreStore.safeGetStore('agentStore');
      const { loadingInAgent, inQueue, inAgent } = agentStore;

      if (loadingInAgent || inQueue || inAgent) {
        sessionStore.HIDE_LOADING();
      }

      console.error(error);
      sessionStore.HIDE_LOADING();

      clearTimeout(timeout);
      // 由于现在很多地方都是判断 res 是否为空来决定请求是否成功, 先不返回 error
      // return;
      // 特殊错误处理
    });
};

// 处理请求返回
export const handleResponse = async (
  { res = {}, req = {}, isFirstInitSession = false, isInitSession = false },
  useAfterDialogueShow = true,
) => {
  // res可能为null
  if (!res) {
    return;
  }

  const {
    dialogue_id,
    taskbot_name,
    taskbot_id,
    is_dialogue_end,
    replies = [],
    node_point_id = '',
    kb_bot_id,
    kb_bot_name,
    inbound_order_sn = '',
    session_id,
  } = res;
  sessionStore.updateCurFunction(res.function);

  // 等待store装载完毕
  const firstScreenResponse = isFirstInitSession || isInitSession;
  if (!firstScreenResponse) {
    await coreStore.fullStorePromise;
  }

  const initStore = coreStore.safeGetStore('initStore');
  const { userId } = coreStore.safeGetStore('userStore');

  let dialogue_type = res.dialogue_type;
  if (dialogue_type === DIALOGUE_TYPE.TASK) {
    dialogue_type = taskbot_name;

    sessionStore.UPDATE_TASK_BOT({
      taskBotId: taskbot_id,
      isTaskBotEnded: is_dialogue_end,
    });
  }

  // 更新最新的 dialogueInfo，跳过 feedback 和 Chit Chat 类型的 dialogue
  if (req.request !== 'postFeedback' && dialogue_type !== DIALOGUE_TYPE.CC) {
    sessionStore.updateDialogueId(dialogue_id);
  }

  const liveAgentControlApiList = Reflect.ownKeys(LIVE_AGENT_CONTROL_API);
  if (liveAgentControlApiList.includes(req.request)) {
    if (isLiveAgentScUIOptimisation() && res.hide_liveagent_shortcuts === LiveAgentControl.show) {
      const shortcutListEle = document.getElementById('shortcuts-list');
      const shortcutListScrollLeft = shortcutListEle.scrollLeft;

      const doneCallback = () => {
        initStore.UPDATE_HIDE_LIVE_AGENT_SHORTCUTS(LiveAgentControl.show);
        shortcutListEle?.removeEventListener('touchstart', handleTouchStart);
      };
      const { start, cancel } = animate(
        {
          progress: (percentage) => {
            shortcutListEle.scroll(shortcutListScrollLeft - percentage * shortcutListScrollLeft, 0);
          },
          done: doneCallback,
        },
        shortcutListScrollLeft / 2,
      );
      const handleTouchStart = () => {
        cancel();
        doneCallback();
      };

      shortcutListEle?.addEventListener('touchstart', handleTouchStart);

      try {
        start();
      } catch (err) {
        shortcutListEle.scrollLeft = 0;
        doneCallback();
      }
    } else {
      initStore.UPDATE_HIDE_LIVE_AGENT_SHORTCUTS(res.hide_liveagent_shortcuts);
    }
  }

  // 当接口下发 reshow 时 上报记录一下
  if (res.hide_liveagent_shortcuts === LiveAgentControl.show) {
    const sessionID = coreStore.safeGetStore('initStore').sessionId;
    const reportText = `session ID: ${sessionID}`;
    reporter.apiLog(`get live agent shortcut reshow get ${reportText}`, { level: 'debug' });
    reporter.apiBatchReport({ type: 'count', value: { label: 'live_agent_shortcut_reshow_get' }});
  }

  if (dialogue_id && !initStore.shouldNotShowDynamicAnimation) {
    initStore.UPDATE_SHOULD_NOT_SHOW_DYNAMIC_ANIMATION(true);
  }

  // v1.1.2 sessionId通过init获取，不存在sessionId为空的请求了
  // 检查当前store中session_id是否为空
  // 为空则将本次res中session_id赋值
  // if (sessionStore.isSessionIdEmpty) {
  //   sessionStore.UPDATE_SESSIONID(session_id);
  // }

  // 记录激活过的dialogue_type
  dialogue_type && sessionStore.ADD_ACTIVATED_DIALOGUE_TYPE(dialogue_type);

  let hasFeedback = false;

  const manyReplies = Boolean(replies?.length);

  // 循环replies，依次处理每个reply
  replies?.map(async (dialogue, index) => {
    if (dialogue.reply_type === REPLY_TYPE.FEEDBACK) {
      hasFeedback = true;
    }

    if (dialogue.message_extra.hit_feedback) {
      sessionStore.handleHiddenFeedback();
    }

    if (!Object.values(REPLY_TYPE).includes(dialogue.reply_type)) {
      reporter.apiLog(`Invalid reply type: ${dialogue.reply_type}`, {
        level: 'warn',
        dialogue_id,
        dialogue_type,
      });
      return;
    }

    const customCallback = getCallbackParams(dialogue.message_extra);

    const params = {
      dialogue_id,
      dialogue_type,
      dialogue,
      node_point_id,
      taskbot_name,
      kb_bot_id,
      kb_bot_name,
      inbound_order_sn,
      customCallback: { ...customCallback },
    };

    const dialogueContent = RESPONSE_HANDLER[dialogue.reply_type](params, req);
    const isTFECard = dialogue.reply_type > 1000 && dialogue.reply_type < 2000;

    // 判断是否需要展示新版feedback
    const { feedback_info, buttons_for_feedback, live_agent_queue_info, from_type, output_info }
      = dialogue.message_extra;

    if (dialogueContent) {
      dialogueContent.customCallback = { ...customCallback };
      dialogueContent.userInputId = req.userInputId;
      dialogueContent.outputType = output_info;
    }

    // 添加 llm tag
    if (dialogueContent) {
      const outputInfoText = dialogue?.message_extra?.output_info_text ?? '';
      dialogueContent.messageDocQATipText = outputInfoText;
    }

    if (dialogueContent && useUIOptimisationRecommender(replies, res)) {
      dialogueContent.useUIOptimisationRecommender = true;
    }

    if (dialogueContent && useUIOptimisationHotQuestion(dialogue, res)) {
      dialogueContent.useUIOptimisationHotQuestion = true;
    }

    const liveAgentSetting = initStore.liveAgentSetting;
    const liveAgentRecommenderScenario = liveAgentSetting?.allow_live_agent
      ? dialogue.message_extra.live_agent_recommender_scenario
      : LiveAgentRecommenderScenario.GoOldLogic;

    if (dialogueContent && feedback_info) {
      dialogueContent.feedbackInfo = {
        ...feedback_info,
        options: buttons_for_feedback,
        tfeRatingInfo: isTFECard
          ? {
            region: COUNTRY.toLowerCase(),
            session_id,
            dialogue_id,
            dm_thread_id: dialogue?.message_extra?.thread_id,
            task_bot_info: dialogueContent?.data?.taskbotInfo,
          }
          : undefined,
      };

      // 把feedback的数据加进feedbackInfo，传给FeedbackRight上报
      if (replies?.[0]?.message_extra?.task_flow_text?.webform_report_data) {
        dialogueContent.feedbackInfo = {
          ...dialogueContent.feedbackInfo,
          webformReportData: {
            ...replies?.[0]?.message_extra?.task_flow_text?.webform_report_data,
            ...dialogueContent?.data?.taskbotInfo,
            region: COUNTRY,
            session_id,
            dialogue_id,
          },
        };
      } else if (replies?.[0]?.message_extra?.control_message?.webform_report_data) {
        dialogueContent.feedbackInfo = {
          ...dialogueContent.feedbackInfo,
          webformReportData: {
            ...replies?.[0]?.message_extra?.control_message?.webform_report_data,
            ...dialogueContent?.data?.taskbotInfo,
            region: COUNTRY,
            session_id,
            dialogue_id,
          },
        };
      }
    }

    if (dialogueContent) {
      const { dialogueType } = dialogueContent;
      const { userId } = coreStore.safeGetStore('userStore');
      // 未登录时触发 IDK
      const isNotLoginWithIDK = !userId && dialogueType === DIALOGUE_TYPE.IDK;

      if (
        !isNotLoginWithIDK
        && dialogueContent.type === DIALOGUE_TYPE.Live
        && liveAgentRecommenderScenario
        && liveAgentRecommenderScenario !== LiveAgentRecommenderScenario.GoOldLogic
      ) {
        const {
          dialogueId,
          data: { liveAgentAutoQueueInfo: { let_user_choose: letUserChoose = true, queue_type: queueType }},
        } = dialogueContent;
        const { chat, hasEndLiveChat, UPDATE_HAS_END_LIVECHAT } = coreStore.safeGetStore('agentStore');
        const channelType = await chat.getChannelType();
        const isAutoQueue
          = letUserChoose === false && channelType === ChannelType.inhouse && queueType === QueueType.inhouse;

        if (!isAutoQueue) {
          const {
            queue_category_id: userType = '',
            queue_category: userTypeName = '',
            queue_name: enquiryType = '',
            queue_from,
            // hasEndLiveChat：重新进线时不使用上一次选择的userType和enquiryType
          } = !hasEndLiveChat
          && (dialogueType === DIALOGUE_TYPE.IDK
            || liveAgentRecommenderScenario === LiveAgentRecommenderScenario.GoDirectlyToAgent)
            ? live_agent_queue_info ?? {}
            : {};
          // @MARK: lazyLoad: { handleLiveBtnClick }
          import('@/utils/btnClickHandler').then(({ handleLiveBtnClick }) => {
            handleLiveBtnClick({
              dialogueId,
              dialogueType,
              queue_name: enquiryType,
              user_type: userType ? Number(userType) : userType,
              enquiry_type: queue_from === ChannelType.inhouse ? Number(enquiryType) : enquiryType,
              userTypeName,
              triggerLiveAgentRecommender: true,
              showProbingForm: from_type !== AutoQueueFromType.NoAnyDialogue,
            });
          });

          // 保存进线人工客服时的 dialogueType，以便在 Live Agent Recommender 的 solution 接口失败，直接进线时使用
          sessionStore.UPDATE_DIALOGUE_TYPE_FOR_LIVE_CHAT(dialogueType);
          UPDATE_HAS_END_LIVECHAT(false);
          return;
        }
      }

      // 一般情况下 有多个 card 而且有 article 时 这 article 的类型是 tfe article
      // 当 tfe article 不是第一个时 就不需要自动弹出
      if (manyReplies && index > 0 && dialogueContent.type === DIALOGUE_TYPE.TFEArticle) {
        dialogueContent.data.shouldDisplayInstantly = false;
      }

      const FaqType = [REPLY_TYPE.NEW_HOT_QUESTION, REPLY_TYPE.TFE_RELATED_INTENT];
      const ArticleType = [REPLY_TYPE.TFE_ARTICLE, REPLY_TYPE.ARTICLE];
      if (!isSeparate() && !isOldUI()) {
        if (
          manyReplies
          && ArticleType.includes(dialogue?.reply_type)
          && FaqType.includes(replies?.[index + 1]?.reply_type)
        ) {
          const isNewHotQuestion = replies?.[index + 1]?.reply_type === REPLY_TYPE.NEW_HOT_QUESTION;
          const { message, message_extra } = replies?.[index + 1];
          const faqTitle = isNewHotQuestion ? message.text_value : message_extra?.task_flow_related_intent?.content;
          const faqIntentList = isNewHotQuestion
            ? message_extra?.cat_intent_list?.intents
            : message_extra?.task_flow_related_intent?.intents?.map((item) => {
              return {
                ...item,
                query_callback: {
                  session_id: coreStore.safeGetStore('initStore').sessionId,
                  kb_bot_id: item.kb_bot_id,
                  intent_id: item.intent_id,
                  source: IntentSource.RelatedIntent,
                  is_category: false,
                  thread_node_id: message_extra.thread_node_id,
                  thread_id: message_extra.thread_id,
                  thread_id_v2: message_extra.thread_id_v2,
                  round_id: message_extra.round_id,
                  is_bypass: false,
                  dialogue_id: dialogue_id,
                },
              };
            });

          dialogueContent.data.faq = { faqTitle, faqIntentList: Array.from(faqIntentList) };
        }
        if (
          manyReplies
          && index > 0
          && FaqType.includes(dialogue?.reply_type)
          && ArticleType.includes(replies?.[index - 1]?.reply_type)
        ) {
          dialogueContent.hidden = true;
        }
      }
      const initSessionCardShowAnimation
        = isInitSession && !['Announcements', 'HotQuestionCategory'].includes(dialogueContent.type);

      // 填充渲染组件
      if (isBatchShowSkeleton() && firstScreenResponse) {
        // 检查是否需要替换对应的首屏骨架组件，如果是的话先暂存，后续统一替换
        stashInitSessionDialogue(dialogueContent, isFirstInitSession);
      } else {
        // 正常填充组件
        const curPos = addDialogue(
          {
            ...dialogueContent,
            _useAfterDialogueShow_: useAfterDialogueShow,
            showAnimation: initSessionCardShowAnimation,
          },
          isInitSession ? false : undefined,
        );
        dialogueContent?.callback?.(curPos);
      }
    }
  });

  if (isBatchShowSkeleton() && firstScreenResponse) {
    replaceSkeletonDialogues(isFirstInitSession);
  }

  !hasFeedback && sessionStore.currentFeedback && sessionStore.UPDATE_CUR_FEEDBACK();
};
